
/* eslint-disable class-methods-use-this */
import { Component, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { SessionUser } from '@/core/types/session-user';
import { ModalState } from '@/core/types/modal-state';
import { ProfileLinkedAccount } from '@/profile/types/profile-linked-account';
import { ProfileAccount } from '@/profile/types/profile-account';
import { BorrowerAddress } from '@/core/types/borrower-address';
import { AccountType } from '@/core/enums/account-type';
import { CannotDeleteReason } from '@kasasa/kloans-common-ui/dist/consumer-app/services/kloans-iav/types/linked-account';
import { ProfileFI } from '@/profile/types/profile-fi';
import { Plaid } from '@/core/types/plaid';
import ProfileMyProfileEditPinModal from '@/profile/pages/ProfileMyProfile/ProfileMyProfileEditPinModal.vue';
import ProfileMyProfileEditEmailModal from '@/profile/pages/ProfileMyProfile/ProfileMyProfileEditEmailModal.vue';
import ProfileMyProfileEditPasswordModal from '@/profile/pages/ProfileMyProfile/ProfileMyProfileEditPasswordModal.vue';
import ProfileMyProfileEditPhoneModal from '@/profile/pages/ProfileMyProfile/ProfileMyProfileEditPhoneModal.vue';
import ProfileMyProfileDeleteLinkedAccountModal from '@/profile/pages/ProfileMyProfile/ProfileMyProfileDeleteLinkedAccountModal.vue';
import ProfileMyProfileAddLinkedAccountModal from '@/profile/pages/ProfileMyProfile/ProfileMyProfileAddLinkedAccountModal.vue';
import IconWithTooltip from '@/core/components/IconWithTooltip.vue';
import { UtilHelper } from '@/util/helper';
import { ProfileHelper } from '@/profile/helper';

const profileNamespace = namespace('profile');
const coreNamespace = namespace('core');

@Component({
	components: {
		ProfileMyProfileEditPinModal,
		ProfileMyProfileEditEmailModal,
		ProfileMyProfileEditPasswordModal,
		ProfileMyProfileEditPhoneModal,
		ProfileMyProfileDeleteLinkedAccountModal,
		ProfileMyProfileAddLinkedAccountModal,
		IconWithTooltip,
	},
})
export default class ProfileMyProfile extends Vue {
	@profileNamespace.State('accounts')
	public accounts!: ProfileAccount[];

	public accountToDelete: ProfileAccount | null = null;

	public accountToEdit: ProfileAccount | null = null;

	public paymentRefUrl: string = '';

	public addLinkedAccountModalState: ModalState = {
		isShown: false,
	};

	public deleteLinkedAccountModalState: ModalState = {
		isShown: false,
	};

	public editPinModalState: ModalState = {
		isShown: false,
	};

	public editEmailModalState: ModalState = {
		isShown: false,
	};

	public editPhoneModalState: ModalState = {
		isShown: false,
	};

	public editPasswordModalState: ModalState = {
		isShown: false,
	};

	public formatPhone = UtilHelper.formatPhoneNumber;

	@profileNamespace.Getter('hasLoanAccounts')
	public hasLoanAccounts!: boolean;

	@profileNamespace.State('institutions')
	public institutions!: ProfileFI[];

	@profileNamespace.State('loanLinkedAccounts')
	public loanLinkedAccounts!: ProfileLinkedAccount[];

	@coreNamespace.Getter('plaid')
	public plaid!: Plaid;

	public selectedLinkedAccountTab = AccountType.LOAN;

	@coreNamespace.Getter('user')
	public user!: SessionUser;

	get accountsBySelectedType(): ProfileLinkedAccount[] {
		return this.loanLinkedAccounts;
	}

	get loanInstitutions(): ProfileFI[] {
		const institutions = ProfileHelper.getInstitutionsFromAccounts({
			accounts: this.accounts,
			type: AccountType.LOAN,
		});

		return institutions;
	}

	mounted(): void {
		if (this.$route.params) {
			if (this.$route.params.modalIsOpen) {
				this.addLinkedAccountModalState = {
					isShown: true,
				};
			}

			if (this.$route.params.paymentRefUrl) {
				this.paymentRefUrl = this.$route.params.paymentRefUrl;
			}
		}
		this.selectedLinkedAccountTab = AccountType.LOAN;
	}

	public deleteAccount(account: ProfileAccount): void {
		this.accountToDelete = account;
		this.deleteLinkedAccountModalState = {
			isShown: true,
		};
	}

	public editEmail(): void {
		this.editEmailModalState = {
			isShown: true,
		};
	}

	public editPassword(): void {
		this.editPasswordModalState = {
			isShown: true,
		};
	}

	public editPhone(): void {
		this.editPhoneModalState = {
			isShown: true,
		};
	}

	public editPin(): void {
		this.editPinModalState = {
			isShown: true,
		};
	}

	public formatAddress(address: BorrowerAddress): string {
		return `
			${address.addressLine1 ?? ''},
			${address.addressLine2 ? `${address.addressLine2} ` : ''}
			${address.city ?? ''} ${address.state ?? ''} ${address.zip ?? ''}
		`;
	}

	public getLinkedAccountLink(account: ProfileLinkedAccount, cannotDeleteReason: CannotDeleteReason): string {
		const isAutoPayReason = cannotDeleteReason.reason.indexOf('Autopay') !== -1;

		return `/#/loan/${cannotDeleteReason.loanId}/${isAutoPayReason ? '' : 'transactions'}`;
	}

	public linkNewAccount(): void {
		if (this.selectedLinkedAccountTab === AccountType.LOAN) {
			this.addLinkedAccountModalState = {
				isShown: true,
			};
		}
	}
}
